/**
 * Presentation Layer - MedicalReservationsUploadContainer Component
 * 診療予約CSVアップロードコンテナコンポーネント
 *
 * 全フック・コンポーネントを統合し、アップロードフロー全体を管理する
 */

import React, { useCallback, useState, useEffect } from 'react';
import {
  Box,
  Alert,
  Snackbar,
  Card,
  CardHeader,
  CardContent,
  Typography,
  CircularProgress,
  LinearProgress,
  Button,
  Tooltip,
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';

// Hooks
import {
  useMedicalReservationsUpload,
  useDataPreview,
  useEditDialog,
  type DataDisplayMode,
} from '../hook';

// Components
import {
  CsvFileDropZone,
  FileInfoDisplay,
  TimeRangeSelector,
  DataPreviewGrid,
  DataPreviewHeader,
  DoctorEditDialog,
  StatusEditDialog,
  UploadButton,
  RegistrationResultAlert,
  DuplicateErrorDialog,
  GoogleSheetsExportResult,
  DataSeparationStatus,
  ResetButton,
} from '../components';
import { NextDayExportButton } from '../components/NextDayExportButton';

// Types
import type { EditableField } from '../../../../domain/medicalReservation/EditableFieldsPolicy';
import type { TimeRange } from '../../../../domain/medicalReservation/TimeRange';

/**
 * Props定義
 */
export interface MedicalReservationsUploadContainerProps {
  /** テスト用: カスタムフックオプション */
  testOptions?: Parameters<typeof useMedicalReservationsUpload>[0];
}

/**
 * 診療予約CSVアップロードコンテナコンポーネント
 */
export const MedicalReservationsUploadContainer: React.FC<
  MedicalReservationsUploadContainerProps
> = ({ testOptions }) => {
  // メインフック
  const [state, actions] = useMedicalReservationsUpload(testOptions ?? {});

  // 編集ダイアログフック（handleStartFieldEditより先に定義）
  const [editDialogState, editDialogActions] = useEditDialog();

  // 重複エラーダイアログ状態
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);

  /**
   * フィールド編集コールバック
   */
  const handleEditField = useCallback(
    (rowIndex: number, field: string, value: string) => {
      // TODO: ProcessingResultの更新ロジックを実装
      console.log('フィールド編集:', { rowIndex, field, value });
    },
    []
  );

  /**
   * フィールド編集開始コールバック（DataPreviewGrid用）
   */
  const handleStartFieldEdit = useCallback(
    (rowIndex: number, field: string) => {
      // 編集可能フィールドかチェック
      if (field === 'doctor' || field === 'status' || field === '担当医師' || field === 'ステータス') {
        const editableField: EditableField = field === 'doctor' || field === '担当医師' ? 'doctor' : 'status';
        editDialogActions.openDialog(rowIndex, editableField, '');
      }
    },
    [editDialogActions]
  );

  // データプレビューフック
  const [previewState, previewActions] = useDataPreview({
    csvData: state.csvData,
    filteredData: state.filteredData,
    processingResult: state.processingResult,
    onEditField: handleStartFieldEdit,
  });

  /**
   * API処理結果が取得されたら自動的に'processed'モードに切り替える
   */
  useEffect(() => {
    if (state.processingResult !== null) {
      previewActions.setDisplayMode('processed');
    }
  }, [state.processingResult, previewActions]);

  /**
   * ファイル選択ハンドラー
   */
  const handleFileSelect = useCallback(
    (file: File) => {
      actions.handleFileSelect(file);
    },
    [actions]
  );

  /**
   * 時間帯変更ハンドラー
   */
  const handleTimeRangeChange = useCallback(
    (newTimeRange: TimeRange) => {
      actions.setTimeRange(newTimeRange);
    },
    [actions]
  );

  /**
   * フィルター実行ハンドラー
   */
  const handleApplyFilter = useCallback(() => {
    actions.handleTimeRangeFilter();
  }, [actions]);

  /**
   * 登録ハンドラー
   * process APIは「時間帯を設定」ボタンで既に呼び出されているため、
   * ここでは登録とエクスポートのみ実行する
   */
  const handleRegister = useCallback(async () => {
    // 登録
    await actions.handleDataRegistration();

    // 時間外データがある場合はエクスポート
    if (state.outsideTimeRangeData && state.outsideTimeRangeData.totalRows > 0) {
      await actions.handleExportOutsideTimeRange();
    }
  }, [actions, state.outsideTimeRangeData]);

  /**
   * 表示モード切り替えハンドラー
   */
  const handleToggleDisplayMode = useCallback(
    (mode: DataDisplayMode) => {
      previewActions.setDisplayMode(mode);
    },
    [previewActions]
  );

  /**
   * 編集保存ハンドラー
   */
  const handleEditSave = useCallback(
    (value: string) => {
      editDialogActions.saveEdit((rowIndex, field, savedValue) => {
        handleEditField(rowIndex, field, savedValue);
      });
    },
    [editDialogActions, handleEditField]
  );

  /**
   * リセットハンドラー
   */
  const handleReset = useCallback(() => {
    actions.resetAll();
    previewActions.setDisplayMode('original');
  }, [actions, previewActions]);

  /**
   * 翌日指定ハンドラー
   */
  const handleSetNextDay = useCallback(() => {
    actions.handleSetNextDay();
  }, [actions]);

  /**
   * 翌日データ出力ハンドラー
   */
  const handleNextDayExport = useCallback(async () => {
    await actions.handleNextDayExport();
  }, [actions]);

  /**
   * 重複ダイアログを閉じる
   */
  const handleCloseDuplicateDialog = useCallback(() => {
    setIsDuplicateDialogOpen(false);
  }, []);

  // 処理中フラグ
  const isProcessingAny =
    state.isLoading || state.isProcessing || state.isRegistering || state.isExporting;

  // アップロードボタンの有効/無効（翌日指定モード中は無効）
  const canUpload =
    state.csvData !== null &&
    state.csvData.totalRows > 0 &&
    !isProcessingAny &&
    !state.isNextDayMode;

  // 翌日データ出力ボタンの有効/無効
  const canNextDayExport =
    state.isNextDayMode &&
    state.nextDayFilteredData !== null &&
    state.nextDayFilteredData.totalRows > 0 &&
    !isProcessingAny;

  // 翌日データ出力ボタンの無効化理由
  const nextDayExportDisabledReason = (() => {
    if (!state.isNextDayMode) return '翌日を指定してください';
    if (!state.nextDayFilteredData || state.nextDayFilteredData.totalRows === 0) {
      return '出力対象のデータがありません';
    }
    return undefined;
  })();

  // フィルターされたデータ件数
  const inRangeCount = state.filteredData?.totalRows ?? 0;
  const outOfRangeCount = state.outsideTimeRangeData?.totalRows ?? 0;

  // 時間範囲の説明テキスト
  const timeRangeDescription = state.timeRange
    ? `${state.timeRange.startTime} ~ ${state.timeRange.endTime}`
    : undefined;

  return (
    <Box>
      {/* エラーメッセージ */}
      {state.errors.length > 0 && (
        <Alert
          severity="error"
          onClose={actions.clearErrors}
          sx={{ mb: 3 }}
        >
          {state.errors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </Alert>
      )}

      {/* ステップ1: ファイル選択 */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="CSVファイルを選択"
          avatar={<CloudUpload color="primary" />}
        />
        <CardContent>
          <CsvFileDropZone
            onFileSelect={handleFileSelect}
            isDisabled={isProcessingAny}
            isLoading={state.isLoading}
          />

          {state.csvFile && (
            <Box sx={{ mt: 2 }}>
              <FileInfoDisplay
                csvFile={state.csvFile}
                csvData={state.csvData}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ステップ2: 時間帯指定 */}
      {state.csvData && (
        <Box sx={{ mb: 3 }}>
          <TimeRangeSelector
            timeRange={state.timeRange}
            onChange={handleTimeRangeChange}
            isDisabled={isProcessingAny}
            isLoading={state.isProcessing}
            onSetNextDay={handleSetNextDay}
          />
          {inRangeCount > 0 && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="success">
                時間帯設定済み: {timeRangeDescription}
              </Alert>
            </Box>
          )}
          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleApplyFilter}
              disabled={isProcessingAny || !state.timeRange.startDate || !state.timeRange.startTime || !state.timeRange.endDate || !state.timeRange.endTime}
              startIcon={state.isProcessing ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {state.isProcessing ? '処理中...' : '時間帯を設定'}
            </Button>
          </Box>
        </Box>
      )}

      {/* ステップ3: データプレビュー */}
      {state.csvData && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="データプレビュー"
            subheader={`${previewState.displayCount}件のデータ`}
            action={
              <DataPreviewHeader
                totalCount={previewState.displayCount}
                displayMode={previewState.displayMode}
                onToggleMode={handleToggleDisplayMode}
                hasProcessedData={state.processingResult !== null}
              />
            }
          />
          <CardContent>
            <Box sx={{ height: 400, width: '100%' }}>
              <DataPreviewGrid
                rows={previewState.displayRows}
                columns={previewState.displayColumns}
                displayMode={previewState.displayMode}
                onEditField={handleStartFieldEdit}
                isLoading={state.isProcessing}
                isNextDayMode={state.isNextDayMode}
                excludedClientCount={state.excludedClientCount}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* アップロード進捗 */}
      {state.isRegistering && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                データをアップロード中...
              </Typography>
              <LinearProgress sx={{ mt: 2 }} />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* アクションボタン */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mb: 3 }}>
        <ResetButton
          onClick={handleReset}
          isDisabled={isProcessingAny}
        />
        {state.isNextDayMode ? (
          <Tooltip title="翌日指定モード中はアップロードできません" arrow>
            <span>
              <UploadButton
                onClick={handleRegister}
                isLoading={state.isRegistering || state.isExporting}
                isDisabled={!canUpload}
                label="アップロード"
                loadingLabel="アップロード中..."
              />
            </span>
          </Tooltip>
        ) : (
          <UploadButton
            onClick={handleRegister}
            isLoading={state.isRegistering || state.isExporting}
            isDisabled={!canUpload}
            label="アップロード"
            loadingLabel="アップロード中..."
          />
        )}
        <NextDayExportButton
          onClick={handleNextDayExport}
          isLoading={state.isExporting}
          isDisabled={!canNextDayExport}
          disabledReason={nextDayExportDisabledReason}
        />
      </Box>

      {/* データ分離状態表示 */}
      {state.filteredData && (
        <Box sx={{ mt: 3 }}>
          <Card>
            <CardHeader title="データ分離状態" />
            <CardContent>
              <DataSeparationStatus
                inRangeCount={inRangeCount}
                outOfRangeCount={outOfRangeCount}
                timeRangeDescription={timeRangeDescription}
              />
            </CardContent>
          </Card>
        </Box>
      )}

      {/* 登録結果 */}
      {state.registrationResult && (
        <Box sx={{ mt: 3 }}>
          <RegistrationResultAlert result={state.registrationResult} />
        </Box>
      )}

      {/* Google Sheetsエクスポート結果 */}
      {state.exportResult && (
        <Box sx={{ mt: 3 }}>
          <GoogleSheetsExportResult result={state.exportResult} />
        </Box>
      )}

      {/* 編集ダイアログ - 医師 */}
      <DoctorEditDialog
        open={editDialogState.isOpen && editDialogState.field === 'doctor'}
        currentValue={editDialogState.currentValue}
        onSave={handleEditSave}
        onCancel={editDialogActions.closeDialog}
      />

      {/* 編集ダイアログ - ステータス */}
      <StatusEditDialog
        open={editDialogState.isOpen && editDialogState.field === 'status'}
        currentValue={editDialogState.currentValue}
        onSave={handleEditSave}
        onCancel={editDialogActions.closeDialog}
      />

      {/* 重複エラーダイアログ */}
      <DuplicateErrorDialog
        open={isDuplicateDialogOpen}
        duplicates={state.duplicateErrors}
        onClose={handleCloseDuplicateDialog}
      />

      {/* スナックバー（エラー通知） */}
      <Snackbar
        open={state.errors.length > 0}
        autoHideDuration={6000}
        onClose={actions.clearErrors}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={actions.clearErrors}>
          {state.errors[0]}
        </Alert>
      </Snackbar>
    </Box>
  );
};
